import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button } from '@mui/material';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import { dateFormatter, enum2NumberArray } from 'core/utils/commonUtils';
import { getOrderPaymentTypeName } from 'core/utils/orderUtils';
import { isNil, gt } from 'lodash';
import { OrderPaymentTypeEnum } from 'models/enums/orderPaymentTypeEnum';
import { useEffect, useState } from 'react';
import {
  FormContainer,
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  useForm,
} from 'react-hook-form-mui';
import { number, object, string } from 'yup';
import type { OrderPaymentInfo } from 'models/order/orderPaymentInfo';
import type { FC } from 'react';
import type { Schema } from 'yup';

interface FormData extends Pick<OrderPaymentInfo, 'type' | 'sum'> {
  remark?: string | null;
}

const schema: Schema<FormData> = object().shape({
  remark: string().max(1000).notRequired(),
  type: number().oneOf(enum2NumberArray(OrderPaymentTypeEnum)).required(),
  sum: number().required().min(0).max(1_000_000_000),
});

const PaymentEditDialog: FC<PaymentEditDialogProps> = ({
  payment,
  open,
  onCancel,
  onSave,
}) => {
  const formContext = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    formContext.reset(payment);
  }, [formContext, payment]);

  const handleOnSave: VoidFunction = () => {
    const editedPayment = {
      ...payment,
      ...formContext.getValues(),
      remark: formContext.getValues('remark') ?? '',
    };

    sendNewPaymentInfoToMetrika(editedPayment);

    if (!isNil(onSave)) {
      setSaving(true);
      onSave(editedPayment)
        .finally(() => {
          setSaving(false);
        })
        .catch(console.log);
    }
  };

  const sendNewPaymentInfoToMetrika = (editedPayment: OrderPaymentInfo): void => {
    if (process.env.NODE_ENV !== 'production' || !process.env.REACT_APP_METRIKA_ACCOUNT) {
      return;
    }

    const ymIds = [
      '',
      'be9e41a5-4c90-4972-a1e1-7952843dc4d2',
      'a49927f4-d374-4f28-a834-cd407017d047',
    ] as const;

    ym(+process.env.REACT_APP_METRIKA_ACCOUNT, 'reachGoal', ymIds[editedPayment.type]);
  };

  const getTitle = (): string =>
    gt(payment.id, 0)
      ? 'Новый платеж'
      : `Платеж от ${dateFormatter.format(payment.dateCreate)}`;

  return (
    <SimpleDialog
      title={getTitle()}
      open={open}
      onClose={onCancel}
      actions={
        <>
          <LoadingButton
            loading={saving}
            onClick={() => {
              void formContext.handleSubmit(handleOnSave)();
            }}>
            <span>Сохранить</span>
          </LoadingButton>
          <Button onClick={onCancel}>Закрыть</Button>
        </>
      }
      content={
        <FormContainer formContext={formContext} onSuccess={handleOnSave}>
          <TextFieldElement
            fullWidth
            label='Сумма'
            name='sum'
            type='number'
            margin='normal'
            required
          />

          <SelectElement
            label='Тип'
            name='type'
            margin='normal'
            fullWidth
            required
            options={enum2NumberArray(OrderPaymentTypeEnum).map((v) => ({
              id: v,
              label: getOrderPaymentTypeName(v),
            }))}
          />

          <TextareaAutosizeElement
            fullWidth
            label='Примечание'
            name='remark'
            margin='normal'
            resizeStyle='vertical'
            rows={2}
          />
        </FormContainer>
      }
    />
  );
};

export interface PaymentEditDialogProps {
  payment: OrderPaymentInfo;
  open: boolean;
  onSave?: (payment: OrderPaymentInfo) => Promise<boolean>;
  onCancel?: VoidFunction;
}

export default PaymentEditDialog;
